import { SalesPriceResponse } from '../types/prices';

/** Prices supersede each other only within the same sales organisation (or
    organisation group), price level, and unit. */
function priceGroupKey(sp: SalesPriceResponse): string {
  return [
    sp.salesOrganisation?.id ?? sp.salesOrganisationGroup?.id ?? '',
    sp.priceLevel?.id ?? '',
    sp.unit?.id ?? '',
  ].join('|');
}

function time(value: string | null | undefined): number | null {
  if (!value) return null;
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : t;
}

function startOfDay(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function displayName(sp: SalesPriceResponse): string {
  return sp.salesOrganisation?.name ?? sp.salesOrganisationGroup?.name ?? '';
}

function compareForDisplay(a: SalesPriceResponse, b: SalesPriceResponse): number {
  return (
    displayName(a).localeCompare(displayName(b), 'de') ||
    (a.priceLevel?.name ?? '').localeCompare(b.priceLevel?.name ?? '', 'de')
  );
}

/** Whether the price's validity has ended before today (`validTo` inclusive —
    a price ending today still counts as valid). */
export function isEndedSalesPrice(sp: SalesPriceResponse, today: Date): boolean {
  const to = time(sp.validTo);
  return to != null && to < startOfDay(today);
}

/** The applicable price per (organisation, level, unit) group. Within a group
    a later `validFrom` supersedes earlier entries, so the newest entry whose
    period covers today wins (an ended promo falls back to the older open-ended
    list price). Groups where NOTHING covers today keep their latest started
    entry — with stale data the last known price is still shown; mark it via
    `isEndedSalesPrice`. Future entries are skipped. */
export function latestSalesPrices(
  prices: SalesPriceResponse[],
  today: Date = new Date()
): SalesPriceResponse[] {
  const now = today.getTime();
  const newer = (a: SalesPriceResponse, b: SalesPriceResponse | undefined) =>
    !b || (time(b.validFrom) ?? -Infinity) < (time(a.validFrom) ?? -Infinity);
  const covering = new Map<string, SalesPriceResponse>();
  const started = new Map<string, SalesPriceResponse>();
  for (const sp of prices) {
    const from = time(sp.validFrom);
    if (from != null && from > now) continue;
    const key = priceGroupKey(sp);
    if (newer(sp, started.get(key))) started.set(key, sp);
    if (!isEndedSalesPrice(sp, today) && newer(sp, covering.get(key)))
      covering.set(key, sp);
  }
  return [...started.entries()]
    .map(([key, latest]) => covering.get(key) ?? latest)
    .sort(compareForDisplay);
}

/** Validity as one readable phrase. Feeding both ends through a formatter that
    renders "—" for null produced "01.02.2026 – —"; an open end is "ab <date>". */
export function formatValidity(
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
  format: (iso: string) => string
): string {
  const from = validFrom ? format(validFrom) : '';
  const to = validTo ? format(validTo) : '';
  if (from && to) return `${from} – ${to}`;
  if (from) return `ab ${from}`;
  if (to) return `bis ${to}`;
  return '';
}

/** Label for a key/name pair. The API fills `name` with "" for units and
    Preisstufen, so the key must win on empty — not only on null. */
export function keyNameLabel(
  v: { key?: string | null; name?: string | null } | null | undefined
): string {
  return v?.name?.trim() || v?.key?.trim() || '';
}

export interface SalesPriceLevelRow {
  id: string;
  /** Preisstufe (its name, else its key). */
  level: string;
  netPrice: number;
  grossPrice: number | null;
  /** Preis je <unit>; only set when `per` is not 1. */
  per: number | null;
  taxIncluded: boolean;
  additionalTerms: string;
  /** Staffelmenge; 0 = Grundpreis. */
  fromAmount: number;
}

export interface SalesPriceGroup {
  id: string;
  /** Verkaufsorganisation (or -gruppe) the prices belong to. */
  org: string;
  /** Its Schlüssel/Nummer (VKFORG), e.g. "999-Zentrale". */
  orgKey: string;
  validity: string;
  unit: string;
  ended: boolean;
  /** Raw `validFrom`, kept for ordering only. */
  validFrom: string | null;
  rows: SalesPriceLevelRow[];
}

/** Groups prices by organisation + validity period, one row per Preisstufe.
    The real payload repeats the same organisation for every Preisstufe of a
    period (5 levels × 5 periods), so a header per price rendered the identical
    organisation and date over and over. */
export function groupSalesPrices(
  prices: SalesPriceResponse[],
  format: (iso: string) => string,
  today: Date
): SalesPriceGroup[] {
  const groups = new Map<string, SalesPriceGroup>();
  for (const sp of prices) {
    const source = sp.salesOrganisation ?? sp.salesOrganisationGroup;
    const org = keyNameLabel(source) || 'Alle Verkaufsorganisationen';
    // The name alone can repeat across organisations — key on the Schlüssel.
    const orgKey = source?.key?.trim() ?? '';
    const key = `${orgKey}|${org}|${sp.validFrom ?? ''}|${sp.validTo ?? ''}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        id: key,
        org,
        orgKey,
        validity: formatValidity(sp.validFrom, sp.validTo, format),
        unit: keyNameLabel(sp.unit),
        ended: isEndedSalesPrice(sp, today),
        validFrom: sp.validFrom ?? null,
        rows: [],
      };
      groups.set(key, group);
    }
    for (const tier of sp.prices ?? []) {
      group.rows.push({
        id: tier.id,
        level: keyNameLabel(sp.priceLevel),
        netPrice: tier.netPrice,
        // null across the whole payload — a bare "brutto" label was worse.
        grossPrice: tier.grossPrice ? tier.grossPrice : null,
        per: tier.per && tier.per !== 1 ? tier.per : null,
        taxIncluded: tier.taxIncluded,
        additionalTerms: tier.additionalTerms?.trim() ?? '',
        fromAmount: tier.fromAmount,
      });
    }
  }
  // Gültige Zeiträume zuerst — nach validFrom allein stand ein abgelaufener
  // Zeitraum mit späterem Beginn über dem aktuell gültigen Preis.
  const groupList = [...groups.values()];
  for (const group of groupList) {
    group.rows.sort(
      (a, b) =>
        a.fromAmount - b.fromAmount ||
        a.level.localeCompare(b.level, 'de', { numeric: true })
    );
  }
  return groupList.sort((a, b) => {
    if (a.ended !== b.ended) return a.ended ? 1 : -1;
    return (b.validFrom ?? '').localeCompare(a.validFrom ?? '');
  });
}

/** Full price history, newest `validFrom` first (entries without one last),
    ties ordered like the latest view. */
export function sortSalesPricesHistory(
  prices: SalesPriceResponse[]
): SalesPriceResponse[] {
  return [...prices].sort((a, b) => {
    const fa = time(a.validFrom) ?? -Infinity;
    const fb = time(b.validFrom) ?? -Infinity;
    if (fa !== fb) return fb - fa;
    return compareForDisplay(a, b);
  });
}
